import { useNavigate, useSearch } from "@tanstack/solid-router";
import { createMutation, createQuery } from "@tanstack/solid-query";
import { Show, createEffect, createSignal } from "solid-js";
import { AvatarUpload } from "~/components/AvatarUpload";
import { OpenToToggles } from "~/components/OpenToToggles";
import { ProfileForm, type ProfileFormValues } from "~/components/ProfileForm";
import { fetchInterests, fetchMemberProfile, fetchSkills, updateMemberProfile } from "~/lib/api";
import { toggleLookingForSuggestion } from "~/lib/looking-for";
import { OPEN_TO_FIELD, type OpenToFlags, type OpenToKey } from "~/lib/open-to";

export function EditMemberProfilePage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const fromEvent = () => (search() as { fromEvent?: string }).fromEvent ?? "";

  const [values, setValues] = createSignal<ProfileFormValues>({
    displayName: "",
    headline: "",
    bio: "",
    company: "",
    jobRole: "",
    lookingFor: "",
    selectedInterests: [],
    selectedSkills: [],
  });
  const [showEmail, setShowEmail] = createSignal(false);
  const [openTo, setOpenTo] = createSignal<OpenToFlags>({});
  const [hydrated, setHydrated] = createSignal(false);

  const profileQuery = createQuery(() => ({
    queryKey: ["member-profile"],
    queryFn: fetchMemberProfile,
  }));

  const interestsQuery = createQuery(() => ({
    queryKey: ["interests"],
    queryFn: fetchInterests,
  }));
  const skillsQuery = createQuery(() => ({
    queryKey: ["skills"],
    queryFn: fetchSkills,
  }));

  createEffect(() => {
    const p = profileQuery.data?.data;
    if (!p || hydrated()) return;
    setValues({
      displayName: p.display_name ?? "",
      headline: p.headline ?? "",
      bio: p.bio ?? "",
      company: p.company ?? "",
      jobRole: p.job_role ?? "",
      lookingFor: p.looking_for ?? "",
      selectedInterests: [...(p.interest_tag_ids ?? [])],
      selectedSkills: [...(p.skill_tag_ids ?? [])],
    });
    setShowEmail(!!p.show_email_to_connections);
    setOpenTo({
      open_to_hiring: !!p.open_to_hiring,
      open_to_job_search: !!p.open_to_job_search,
      open_to_mentoring: !!p.open_to_mentoring,
      open_to_speaking: !!p.open_to_speaking,
    });
    setHydrated(true);
  });

  const saveMutation = createMutation(() => ({
    mutationFn: () => {
      const v = values();
      return updateMemberProfile({
        display_name: v.displayName.trim(),
        headline: v.headline.trim() || undefined,
        bio: v.bio.trim() || undefined,
        company: v.company.trim() || undefined,
        job_role: v.jobRole.trim() || undefined,
        looking_for: v.lookingFor.trim() || undefined,
        show_email_to_connections: showEmail(),
        open_to_hiring: !!openTo().open_to_hiring,
        open_to_job_search: !!openTo().open_to_job_search,
        open_to_mentoring: !!openTo().open_to_mentoring,
        open_to_speaking: !!openTo().open_to_speaking,
        interest_tag_ids: v.selectedInterests,
        skill_tag_ids: v.selectedSkills,
      });
    },
    onSuccess: () => goBack(),
  }));

  function goBack() {
    const event = fromEvent();
    if (event) {
      navigate({ to: "/directory/$eventId", params: { eventId: event } });
      return;
    }
    navigate({ to: "/me" });
  }

  function toggleLookingFor(phrase: string) {
    setValues((prev) => ({
      ...prev,
      lookingFor: toggleLookingForSuggestion(prev.lookingFor, phrase),
    }));
  }

  function toggleSkill(id: string) {
    setValues((prev) => ({
      ...prev,
      selectedSkills: (prev.selectedSkills ?? []).includes(id)
        ? (prev.selectedSkills ?? []).filter((x) => x !== id)
        : (prev.selectedSkills ?? []).length < 15
          ? [...(prev.selectedSkills ?? []), id]
          : prev.selectedSkills,
    }));
  }

  function toggleInterest(id: string) {
    setValues((prev) => ({
      ...prev,
      selectedInterests: prev.selectedInterests.includes(id)
        ? prev.selectedInterests.filter((x) => x !== id)
        : prev.selectedInterests.length < 10
          ? [...prev.selectedInterests, id]
          : prev.selectedInterests,
    }));
  }

  return (
    <section class="page">
      <div class="page-title-block" style={{ padding: "20px 20px 0" }}>
        <p class="status-chip">Your profile</p>
        <h1>Member profile</h1>
        <p class="muted">
          Bio, photo, and interests are shared across Melbourne, Sydney, and future events.
          Directory opt-in is set per conference from the event screen.
        </p>
      </div>

      <div class="card" style={{ margin: "16px 20px" }}>
        <Show when={profileQuery.isLoading}>
          <p class="muted">Loading profile…</p>
        </Show>

        <Show when={profileQuery.data} keyed>
          {(res) => (
            <AvatarUpload
              displayName={res.data?.display_name}
              avatarUrl={res.data?.avatar_url}
            />
          )}
        </Show>

        <label class="switch-row" style={{ "margin-top": "12px" }}>
          <span>Show my login email to accepted connections</span>
          <input
            type="checkbox"
            checked={showEmail()}
            aria-label="Show my login email to accepted connections"
            onChange={(e) => setShowEmail(e.currentTarget.checked)}
          />
        </label>

        <OpenToToggles
          flags={openTo()}
          onToggle={(key: OpenToKey) =>
            setOpenTo((prev) => ({ ...prev, [OPEN_TO_FIELD[key]]: !prev[OPEN_TO_FIELD[key]] }))
          }
        />

        <ProfileForm
          values={values()}
          interests={interestsQuery.data?.data}
          skills={skillsQuery.data?.data}
          showInterests
          showSkills
          error={saveMutation.isError ? (saveMutation.error as Error).message : undefined}
          pending={saveMutation.isPending}
          submitLabel="Save profile"
          onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
          onToggleLookingFor={toggleLookingFor}
          onToggleInterest={toggleInterest}
          onToggleSkill={toggleSkill}
          onSubmit={() => saveMutation.mutate()}
          onCancel={goBack}
        />

        <Show when={fromEvent()}>
          <button
            type="button"
            class="btn btn-ghost"
            style={{ "margin-top": "12px", width: "100%" }}
            onClick={() =>
              navigate({
                to: "/directory/$eventId/edit",
                params: { eventId: fromEvent() },
              })
            }
          >
            Directory settings for this event
          </button>
        </Show>
      </div>
    </section>
  );
}